/** Hotel queries: list (tenant-scoped) + detail. */

import { type UseQueryResult, useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { queryKeys } from '../query-keys';
import { useApiClient } from '../react/ApiClientProvider';
import { type Hotel, hotelSchema } from '../responses';
import { unwrap } from './_helpers';

/** `GET /hotels` — hotels under the tenant the caller can see. */
export function useHotels(tenantId: string): UseQueryResult<Hotel[]> {
  const client = useApiClient();
  return useQuery({
    queryKey: queryKeys.hotels(tenantId),
    queryFn: async () => {
      const result = await client.GET('/api/v1/tenants/{tenantId}/hotels', {
        params: { path: { tenantId } },
        parseAs: 'json',
      });
      return unwrap(result, z.array(hotelSchema));
    },
  });
}

/** `GET /hotels/:hotelId`. */
export function useHotel(tenantId: string, hotelId: string): UseQueryResult<Hotel> {
  const client = useApiClient();
  return useQuery({
    queryKey: queryKeys.hotel(tenantId, hotelId),
    queryFn: async () => {
      const result = await client.GET('/api/v1/tenants/{tenantId}/hotels/{hotelId}', {
        params: { path: { tenantId, hotelId } },
        parseAs: 'json',
      });
      return unwrap(result, hotelSchema);
    },
    enabled: hotelId.length > 0,
  });
}
